import { useMemo, useState } from "react";
import TodoList from "../../components/TodoList";
import SearchableSelect from "../../components/SearchableSelect";
import EmptyState from "../../components/EmptyState";
import { useApi } from "../../hooks/useApi";
import { listEmployees } from "../../api/employeeService";
import { paginatedRows } from "../../utils/pagination";

// Admin todos: their own list, plus a Team tab to add/view/manage any
// employee's list. Assigning to an employee notifies them.
export default function TodosPage() {
  const [tab, setTab] = useState("mine"); // mine | team
  // Default the Team view to everyone's lists; "__all__" is the aggregate.
  const [employeeId, setEmployeeId] = useState("__all__");

  // The whole roster — including employees who have left — so the admin can
  // assign/manage anyone's todos.
  const employees = useApi(() => listEmployees({ per_page: 500, status: "all" }));
  const employeeRows = paginatedRows(employees.data);
  const employeeOptions = useMemo(
    () => [
      { id: "__all__", label: "All employees", hint: "Everyone's lists" },
      ...employeeRows.map((e) => ({
        id: e.id,
        label: e.left_at ? `${e.name} (left)` : e.name,
        hint: e.department?.name,
      })),
    ],
    [employeeRows],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Todos</h1>
          <div className="page-sub">Your checklist, and todos you assign to the team</div>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 12 }}>
        {[
          { id: "mine", label: "My todos" },
          { id: "team", label: "Team" },
        ].map((t) => (
          <button key={t.id} className={`chip ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
        {tab === "team" && (
          <div style={{ marginLeft: "auto", minWidth: 240 }}>
            <SearchableSelect
              value={employeeId}
              onChange={(v) => setEmployeeId(v ?? "")}
              options={employeeOptions}
              placeholder="Select an employee…"
              searchPlaceholder="Search employees…"
              width={240}
            />
          </div>
        )}
      </div>

      {tab === "mine" ? (
        <TodoList params={{}} createExtra={{}} />
      ) : employeeId === "__all__" ? (
        <TodoList
          key="__all__"
          params={{ scope: "all" }}
          canAdd={false}
          showOwner
          emptyHint="No todos across the team yet."
        />
      ) : employeeId ? (
        <TodoList
          key={employeeId}
          params={{ employee_id: employeeId }}
          createExtra={{ employee_id: employeeId }}
          emptyHint="No todos for this employee yet. Add one above to assign it."
        />
      ) : (
        <div className="card">
          <EmptyState
            icon="user"
            title="Pick an employee"
            hint="Choose an employee to view and manage their todos, or assign new ones."
          />
        </div>
      )}
    </>
  );
}
